import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
} from '@mui/material';
import { addDays, isBefore, startOfDay, format as formatDateFns, parse } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { formatDate, formatTime } from '../../utils/dateUtils';

/**
 * Availability calendar component for selecting booking time
 */
const AvailabilityCalendar = ({
  availableSlots = [],
  selectedDate,
  onDateChange,
  selectedSlot,
  onSlotSelect,
  loading = false,
  minBookingDays = 0,
  maxBookingDays = 30,
}) => {
  const { t } = useTranslation();
  const today = startOfDay(new Date());
  const minDate = addDays(today, minBookingDays);
  const maxDate = addDays(today, maxBookingDays);

  // Filter slots for selected date
  const slotsForDate = availableSlots.filter((slot) => {
    // API returns date field as 'YYYY-MM-DD' and slot_start as ISO string
    const slotDateStr = slot.date || formatDate(new Date(slot.slot_start || slot.start), 'yyyy-MM-dd');
    const selectedDateStr = formatDate(selectedDate, 'yyyy-MM-dd');
    return slotDateStr === selectedDateStr;
  });

  const handleDateChange = (dateString) => {
    if (onDateChange && dateString) {
      const newDate = parse(dateString, 'yyyy-MM-dd', new Date());
      onDateChange(newDate);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
      {/* Date Picker */}
      <Paper sx={{ p: 2, flex: { md: '0 0 320px' } }}>
        <Typography variant="h6" gutterBottom>
          {t('booking.selectDate')}
        </Typography>
        <TextField
          type="date"
          value={selectedDate ? formatDateFns(selectedDate, 'yyyy-MM-dd') : ''}
          onChange={(e) => handleDateChange(e.target.value)}
          inputProps={{
            min: formatDateFns(minDate, 'yyyy-MM-dd'),
            max: formatDateFns(maxDate, 'yyyy-MM-dd'),
          }}
          fullWidth
          sx={{ mt: 2 }}
          InputLabelProps={{ shrink: true }}
        />
      </Paper>

      {/* Time slots */}
      <Paper sx={{ p: 2, flex: 1 }}>
        <Typography variant="h6" gutterBottom>
          {t('booking.selectTime')}
        </Typography>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {!loading && selectedDate && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {formatDate(selectedDate, 'EEEE, MMMM d, yyyy')}
          </Typography>
        )}

        {!loading && slotsForDate.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('booking.noSlotsAvailable')}
          </Typography>
        ) : !loading ? (
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
              gap: 1.5,
            }}
          >
            {slotsForDate.map((slot, index) => {
              const startTime = new Date(slot.slot_start || slot.start);
              const slotKey = slot.slot_start || slot.start;
              const isSelected = selectedSlot?.slot_start === slot.slot_start || selectedSlot?.start === slot.start;
              
              return (
                <Button
                  key={index}
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={() => onSlotSelect(slot)}
                  sx={{
                    py: 1.5,
                    fontWeight: isSelected ? 'bold' : 'normal',
                  }}
                >
                  {formatTime(startTime, 'HH:mm')}
                </Button>
              );
            })}
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
};

export default AvailabilityCalendar;
